import React, { useState, useEffect, useMemo } from "react";
import CardHead from "../../utils/CardHead";
import ProcumentCalcLogic from "../ProcumentCalcLogic";
import ProcurementCharts from "../ProcurementCharts";
import MedicineProcurementSection from "../MedicineProcurementSection";
import { fetchProcurementSummary } from "../../data/api";

const Procurement = () => {
  const [skuData, setSkuData] = useState([]);

  useEffect(() => {
    fetchProcurementSummary().then(r => setSkuData(r.data)).catch(console.error);
  }, []);

  const { rows, totals } = useMemo(() => {
    let totalAmc = 0, totalReq = 0, totalStock = 0, totalPipe = 0, totalProc = 0;

    const computedRows = skuData.map((d) => {
      const amc = parseFloat(d.amc) || 0;
      const stock = parseInt(d.stock) || 0;
      const pipeline = parseInt(d.pipeline) || 0;
      const req = Math.round(amc * 4);
      const coverageValues = amc > 0 ? (stock / amc).toFixed(1) : '0.0';
      const procQty = Math.max(0, req - stock - pipeline);

      totalAmc += amc;
      totalReq += req;
      totalStock += stock;
      totalPipe += pipeline;
      totalProc += procQty;

      const covColor = coverageValues >= 4 ? "var(--green)" : coverageValues >= 2 ? "var(--amber)" : "var(--red)";
      let actionObj = { text: "✔ Sufficient", color: "var(--green)" };
      if (procQty > 0) actionObj = { text: "⚠ Procure", color: "var(--red)" };
      else if (parseFloat(coverageValues) > 6) actionObj = { text: "↔ Redistribute", color: "var(--blue)" };
      const rowBg = procQty > 0 ? "#fff5f5" : "";

      return { name: d.name, cat: d.cat, amc, stock, pipeline, req, coverageValues, procQty, covColor, actionObj, rowBg };
    });

    return { rows: computedRows, totals: { totalAmc, totalReq, totalStock, totalPipe, totalProc } };
  }, [skuData]);

  const downloadProcurementCSV = () => {
    const headers = ["Item Name", "Category", "AMC", "Required (4M)", "Current Stock", "Pipeline", "Coverage (Months)", "Procurement Qty"];
    const csvContent = [
      headers.join(","),
      ...rows.map((row) => [row.name, row.cat, Math.round(row.amc), row.req, row.stock, row.pipeline, row.coverageValues, row.procQty].join(","))
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "procurement_data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Transform for ProcurementCharts (expects dist/amc/stock/pipeline format)
  const chartData = rows.map(r => ({ dist: r.name.substring(0, 20), amc: Math.round(r.amc), stock: r.stock, pipeline: r.pipeline }));

  return (
    <div className={`viz-pane active`}>
      <div className="card">
        <CardHead icon="fas fa-brain" title="Procurement Calculation Logic"
          subtitle="Formula-based procurement quantity estimation" />
        <div className="card-body"><ProcumentCalcLogic /></div>
      </div>

      <div className="card" style={{ marginTop: "20px" }}>
        <CardHead icon="fas fa-table" title="SKU-wise Procurement Analysis"
          subtitle={`Top ${rows.length} SKUs by consumption`} onClick={downloadProcurementCSV} />
        <div className="card-body" style={{ overflowX: "auto", padding: 0 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".8rem" }} id="procurementTable">
            <thead>
              <tr style={{ background: "var(--navy)", color: "#fff" }}>
                <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: "600" }}>Item Name</th>
                <th style={{ padding: "10px 14px", textAlign: "center", fontWeight: "600" }}>Cat</th>
                <th style={{ padding: "10px 14px", textAlign: "right", fontWeight: "600" }}>AMC</th>
                <th style={{ padding: "10px 14px", textAlign: "right", fontWeight: "600" }}>Required (4M)</th>
                <th style={{ padding: "10px 14px", textAlign: "right", fontWeight: "600" }}>Current Stock</th>
                <th style={{ padding: "10px 14px", textAlign: "right", fontWeight: "600" }}>Pipeline</th>
                <th style={{ padding: "10px 14px", textAlign: "right", fontWeight: "600" }}>Coverage (Mo)</th>
                <th style={{ padding: "10px 14px", textAlign: "right", fontWeight: "600" }}>Procurement Qty</th>
                <th style={{ padding: "10px 14px", textAlign: "center", fontWeight: "600" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx} style={{ background: row.rowBg ? row.rowBg : undefined }}>
                  <td style={{ padding: "8px 14px", borderBottom: "1px solid #eee", maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.name}</td>
                  <td style={{ padding: "8px 14px", borderBottom: "1px solid #eee", textAlign: "center" }}>
                    <span style={{ background: row.cat === 'EDL' ? '#dbeafe' : '#fef3c7', padding: '2px 8px', borderRadius: 4, fontWeight: 600, fontSize: '.65rem' }}>{row.cat}</span>
                  </td>
                  <td style={{ padding: "8px 14px", borderBottom: "1px solid #eee", textAlign: "right" }}>{Math.round(row.amc).toLocaleString()}</td>
                  <td style={{ padding: "8px 14px", borderBottom: "1px solid #eee", textAlign: "right" }}>{row.req.toLocaleString()}</td>
                  <td style={{ padding: "8px 14px", borderBottom: "1px solid #eee", textAlign: "right" }}>{row.stock.toLocaleString()}</td>
                  <td style={{ padding: "8px 14px", borderBottom: "1px solid #eee", textAlign: "right" }}>{row.pipeline.toLocaleString()}</td>
                  <td style={{ padding: "8px 14px", borderBottom: "1px solid #eee", textAlign: "right", fontWeight: "700", color: row.covColor }}>{row.coverageValues}</td>
                  <td style={{ padding: "8px 14px", borderBottom: "1px solid #eee", textAlign: "right", fontWeight: "700" }}>{row.procQty > 0 ? row.procQty.toLocaleString() : "—"}</td>
                  <td style={{ padding: "8px 14px", borderBottom: "1px solid #eee", textAlign: "center" }}>
                    <span style={{ color: row.actionObj.color, fontWeight: "700" }}>{row.actionObj.text}</span>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={9} style={{textAlign:'center',padding:20,color:'#999'}}>Loading...</td></tr>}
            </tbody>
            <tfoot>
              <tr style={{ background: "#f0f4fa", fontWeight: "700" }}>
                <td style={{ padding: "10px 14px" }}>TOTAL</td>
                <td style={{ padding: "10px 14px" }}></td>
                <td style={{ padding: "10px 14px", textAlign: "right" }}>{Math.round(totals.totalAmc).toLocaleString()}</td>
                <td style={{ padding: "10px 14px", textAlign: "right" }}>{totals.totalReq.toLocaleString()}</td>
                <td style={{ padding: "10px 14px", textAlign: "right" }}>{totals.totalStock.toLocaleString()}</td>
                <td style={{ padding: "10px 14px", textAlign: "right" }}>{totals.totalPipe.toLocaleString()}</td>
                <td style={{ padding: "10px 14px", textAlign: "right" }}>—</td>
                <td style={{ padding: "10px 14px", textAlign: "right", color: "var(--red)" }}>{totals.totalProc.toLocaleString()}</td>
                <td style={{ padding: "10px 14px", textAlign: "center" }}>—</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <ProcurementCharts data={chartData} />
      <div className="card" style={{ width: "100%", marginTop: "20px" }}>
        <CardHead title="Medicine-wise Procurement Summary" subtitle="Top medicines requiring procurement" />
        <div className="card-body"><MedicineProcurementSection /></div>
      </div>
    </div>
  );
};

export default Procurement;
